import React, { useEffect, useMemo, useRef, useState } from "react";
import PrimaryButton from "./ui/PrimaryButton";
import "./OnboardingTour.css";

const TOUR_STORAGE_KEY = "earlyspot_onboarding_seen_v1";

type Step = {
  target: string;
  title: string;
  description: string;
};

type Placement = "right" | "top" | "bottom";

type TooltipState = {
  top: number;
  left: number;
  placement: Placement;
  arrowOffsetX: number;
  arrowOffsetY: number;
};

const steps: Step[] = [
  {
    target: "dashboard",
    title: "Dashboard overview",
    description:
      "View your latest scan insights, reminders, and helpful actions to stay on top of your skin checks.",
  },
  {
    target: "scan",
    title: "Start a scan",
    description:
      "Head to the Scan tab to capture or upload a lesion photo. We guide you on lighting, clarity, and next steps.",
  },
  {
    target: "results",
    title: "Results & history",
    description:
      "The Results tab stores your assessments, trends, and dermatologist-ready notes for every scan.",
  },
  {
    target: "uv-index",
    title: "UV safety insights",
    description:
      "Check the UV Index tab for real-time exposure data and protective recommendations tailored to your location.",
  },
  {
    target: "profile",
    title: "Profile & settings",
    description:
      "Manage your account, care plan, and support resources from the Profile tab whenever you need.",
  },
];

const OnboardingTour: React.FC = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const scrollPositionRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const seen = window.localStorage.getItem(TOUR_STORAGE_KEY);
    if (!seen) {
      const timer = window.setTimeout(() => setIsOpen(true), 600);
      return () => window.clearTimeout(timer);
    }
  }, []);

  const currentStep = useMemo(() => steps[stepIndex], [stepIndex]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const body = document.body;

    scrollPositionRef.current = {
      x: window.pageXOffset,
      y: window.pageYOffset,
    };

    body.style.position = "fixed";
    body.style.top = "0";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.touchAction = "none";

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.touchAction = "";

      if (scrollPositionRef.current) {
        window.scrollTo({
          top: scrollPositionRef.current.y,
          left: scrollPositionRef.current.x,
          behavior: "auto",
        });
        scrollPositionRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTooltip(null);
      return;
    }

    const selector = `[data-tour="${steps[stepIndex].target}"]`;
    let highlighted: HTMLElement[] = [];

    const getTargets = () => {
      const nodes = Array.from(
        document.querySelectorAll<HTMLElement>(selector)
      );
      return nodes.filter((node) => {
        const rect = node.getBoundingClientRect();
        const style = window.getComputedStyle(node);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      });
    };

    const updatePosition = () => {
      const tooltipHeightEstimate = 240;
      const tooltipWidthEstimate = Math.min(340, window.innerWidth - 32);

      highlighted.forEach((node) =>
        node.classList.remove("tour-highlight")
      );
      highlighted = getTargets();

      if (!highlighted.length) {
        setTooltip(null);
        return;
      }

      highlighted.forEach((node) => node.classList.add("tour-highlight"));

      const primary = highlighted[0];
      const rect = primary.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportWidth = window.innerWidth;

      if (isMobile) {
        const targetCenterX = rect.left + scrollX + rect.width / 2;
        const halfTooltip = tooltipWidthEstimate / 2;
        const minCenterX = scrollX + halfTooltip + 16;
        const maxCenterX = scrollX + viewportWidth - halfTooltip - 16;
        const constrainedCenterX = Math.max(
          minCenterX,
          Math.min(targetCenterX, maxCenterX)
        );

        const anchorOffset = 28;
        const top = rect.top + scrollY - anchorOffset;
        const arrowOffsetX = targetCenterX - constrainedCenterX;

        setTooltip({
          top,
          left: constrainedCenterX,
          placement: "top",
          arrowOffsetX,
          arrowOffsetY: 0,
        });
        return;
      }

      let placement: Placement = "right";
      let top = rect.top + scrollY + rect.height / 2;
      let left = rect.right + scrollX + 24;
      let arrowOffsetX = 0;
      let arrowOffsetY = 0;

      const isSidebarTarget = rect.left < window.innerWidth * 0.4;
      const isBottomTarget = rect.top > window.innerHeight * 0.6;

      if (!isSidebarTarget) {
        placement = isBottomTarget ? "top" : "bottom";
        if (placement === "bottom") {
          top = rect.bottom + scrollY + 24;
        } else {
          top = rect.top + scrollY - 24;
          if (top < scrollY + 32) {
            top = rect.bottom + scrollY + 24;
            placement = "bottom";
          }
        }
        left = rect.left + scrollX + rect.width / 2;
      } else {
        const tooltipHalf = tooltipHeightEstimate / 2;
        const viewportTop = scrollY + 40;
        const viewportBottom = scrollY + window.innerHeight - 40;
        const targetCenter = rect.top + scrollY + rect.height / 2;

        top = targetCenter;

        if (top - tooltipHalf < viewportTop) {
          top = viewportTop + tooltipHalf;
        } else if (top + tooltipHalf > viewportBottom) {
          top = viewportBottom - tooltipHalf;
        }

        arrowOffsetY = targetCenter - top;
        left = rect.right + scrollX + 24;
      }

      if (placement === "bottom" || placement === "top") {
        arrowOffsetX = rect.left + scrollX + rect.width / 2 - left;
        arrowOffsetY = 0;
      }

      setTooltip({
        top,
        left,
        placement,
        arrowOffsetX,
        arrowOffsetY,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      highlighted.forEach((node) =>
        node.classList.remove("tour-highlight")
      );
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, stepIndex, isMobile]);

  const closeTour = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(TOUR_STORAGE_KEY, "true");
    }
    setIsOpen(false);
  };

  const handleNext = () => {
    if (stepIndex === steps.length - 1) {
      closeTour();
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  if (!isOpen || !tooltip) {
    return null;
  }

  const tooltipWidth =
    typeof window !== "undefined"
      ? Math.min(340, window.innerWidth - 32)
      : 340;

  const tooltipStyle: React.CSSProperties = {
    width: tooltipWidth,
    top: tooltip.top,
    left: tooltip.left,
    "--arrow-offset-x": `${tooltip.arrowOffsetX}px`,
    "--arrow-offset-y": `${tooltip.arrowOffsetY}px`,
  } as React.CSSProperties;

  return (
    <div className="tour-overlay" role="presentation">
      <div className="tour-backdrop" />
      <div
        className={`tour-tooltip tour-tooltip--${tooltip.placement}`}
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-description"
      >
        <button type="button" className="tour-skip" onClick={closeTour}>
          Skip tour
        </button>
        <span className="tour-step-indicator">
          Step {stepIndex + 1} of {steps.length}
        </span>
        <h2 id="tour-title">{currentStep.title}</h2>
        <p id="tour-description">{currentStep.description}</p>
        <div className="tour-dots">
          {steps.map((_, idx) => (
            <span
              key={`tour-dot-${idx}`}
              className={`tour-dot ${idx === stepIndex ? "tour-dot--active" : ""}`}
              aria-hidden
            />
          ))}
        </div>
        <div className="tour-actions">
          <button
            type="button"
            className="tour-secondary"
            onClick={handlePrevious}
            disabled={stepIndex === 0}
          >
            Back
          </button>
          <PrimaryButton onClick={handleNext}>
            {stepIndex === steps.length - 1 ? "Got it" : "Next"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;

